<template>
  <div class="gl-page">

    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Riders</h1>
        <p class="gl-page-sub">Manage delivery riders across all outlets</p>
      </div>
      <button class="gl-btn-primary" @click="openCreateModal">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Rider
      </button>
    </div>

    <!-- Stat cards skeleton -->
    <div v-if="loading" class="gl-stats-row">
      <div v-for="i in 4" :key="i" class="gl-stat-card">
        <div class="gl-skel" style="width:80px;height:10px;border-radius:5px;margin-bottom:10px"></div>
        <div class="gl-skel" style="width:50px;height:28px;border-radius:7px"></div>
      </div>
    </div>

    <!-- Stat cards -->
    <div v-else class="gl-stats-row">
      <div class="gl-stat-card">
        <div class="gl-stat-icon" style="background:#e8f4f1">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#900C27" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Total Riders</div>
          <div class="gl-stat-value">{{ riderStats.total || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon" style="background:#dcfce7">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Active</div>
          <div class="gl-stat-value" style="color:#16a34a">{{ riderStats.active || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon" style="background:#e0f2fe">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Available Now</div>
          <div class="gl-stat-value" style="color:#0284c7">{{ riderStats.by_status?.available || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon" style="background:#fef9c3">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Busy</div>
          <div class="gl-stat-value" style="color:#ca8a04">{{ riderStats.by_status?.busy || 0 }}</div>
        </div>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="gl-filter-bar">
      <div class="gl-search-wrap">
        <svg class="gl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input class="gl-search" type="text" placeholder="Search riders…" v-model="filters.search" @input="debounceSearch">
      </div>
      <select class="gl-select" v-model="filters.status" @change="fetchRiders()">
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="available">Available</option>
        <option value="busy">Busy</option>
        <option value="offline">Offline</option>
      </select>
      <select class="gl-select" v-model="filters.vehicle_type" @change="fetchRiders()">
        <option value="">All Vehicles</option>
        <option value="motorcycle">Motorcycle</option>
        <option value="car">Car</option>
        <option value="bicycle">Bicycle</option>
      </select>
    </div>

    <!-- Table card -->
    <div class="gl-card">

      <!-- Skeleton table -->
      <template v-if="loading">
        <div class="gl-tbl-wrap">
          <table class="gl-tbl">
            <thead><tr>
              <th>Rider</th><th>Contact</th><th>Outlet</th><th>Vehicle</th>
              <th>Status</th><th>Deliveries</th><th>Earnings</th><th>Rate</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="i in 7" :key="i">
                <td><div style="display:flex;align-items:center;gap:10px">
                  <div class="gl-skel" style="width:38px;height:38px;border-radius:50%;flex-shrink:0"></div>
                  <div><div class="gl-skel" style="width:100px;height:10px;border-radius:5px;margin-bottom:6px"></div><div class="gl-skel" style="width:40px;height:8px;border-radius:4px"></div></div>
                </div></td>
                <td><div class="gl-skel" style="width:90px;height:10px;border-radius:5px"></div></td>
                <td><div class="gl-skel" style="width:80px;height:10px;border-radius:5px"></div></td>
                <td><div class="gl-skel" style="width:70px;height:22px;border-radius:6px"></div></td>
                <td><div class="gl-skel" style="width:60px;height:22px;border-radius:10px"></div></td>
                <td><div class="gl-skel" style="width:30px;height:10px;border-radius:5px"></div></td>
                <td><div class="gl-skel" style="width:60px;height:10px;border-radius:5px"></div></td>
                <td><div class="gl-skel" style="width:50px;height:22px;border-radius:10px"></div></td>
                <td><div class="gl-skel" style="width:80px;height:30px;border-radius:8px"></div></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Desktop table -->
      <template v-else-if="riders.length">
        <div class="gl-tbl-wrap">
          <table class="gl-tbl">
            <thead><tr>
              <th>Rider</th><th>Contact</th><th>Outlet</th><th>Vehicle</th>
              <th>Status</th><th>Deliveries</th><th>Earnings</th><th>Success Rate</th><th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="rider in riders" :key="rider.id">
                <td>
                  <div class="gl-rl-rider-cell">
                    <div class="gl-rl-avatar">{{ initials(rider.name) }}</div>
                    <div>
                      <div class="gl-rl-name">{{ rider.name }}</div>
                      <div class="gl-rl-id">#{{ rider.id }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="gl-rl-contact">{{ rider.phone }}</div>
                  <div class="gl-rl-sub">{{ rider.email || '—' }}</div>
                </td>
                <td>
                  <div>{{ rider.outlet?.name || '—' }}</div>
                  <div v-if="rider.outlet?.company" class="gl-rl-sub">{{ rider.outlet.company.name }}</div>
                </td>
                <td>
                  <span class="gl-badge gl-badge-dark">{{ vehicleEmoji(rider.vehicle_type) }} {{ rider.vehicle_type }}</span>
                  <div class="gl-rl-sub">{{ rider.vehicle_plate || '—' }}</div>
                </td>
                <td><span class="gl-badge" :class="statusBadgeClass(rider.status, rider.is_active)">{{ rider.is_active ? rider.status : 'inactive' }}</span></td>
                <td>{{ rider.delivery_stats?.total_deliveries || 0 }}</td>
                <td>RM {{ formatCurrency(rider.delivery_stats?.total_earned) }}</td>
                <td><span class="gl-badge" :class="rateBadgeClass(rider.delivery_stats?.success_rate)">{{ rider.delivery_stats?.success_rate || 0 }}%</span></td>
                <td>
                  <div class="gl-rl-actions">
                    <button class="gl-icon-btn" title="View" @click="viewRider(rider)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    <button class="gl-icon-btn" title="Edit" @click="editRider(rider)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button class="gl-icon-btn" :class="rider.is_active ? 'gl-icon-btn-warn' : 'gl-icon-btn-ok'" :title="rider.is_active ? 'Deactivate' : 'Activate'" @click="toggleRider(rider)">
                      <svg v-if="rider.is_active" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="gl-mob-list">
          <div v-for="rider in riders" :key="'m'+rider.id" class="gl-rl-mob-card">
            <div class="gl-rl-mob-top">
              <div class="gl-rl-avatar gl-rl-avatar-lg">{{ initials(rider.name) }}</div>
              <div class="gl-rl-mob-info">
                <div class="gl-rl-name">{{ rider.name }}</div>
                <div class="gl-rl-sub">{{ rider.phone }}</div>
                <div class="gl-rl-sub">{{ rider.outlet?.name || '—' }}</div>
              </div>
              <span class="gl-badge" :class="statusBadgeClass(rider.status, rider.is_active)">{{ rider.is_active ? rider.status : 'inactive' }}</span>
            </div>
            <div class="gl-rl-mob-stats">
              <div class="gl-rl-mob-stat">
                <div class="gl-rl-mob-stat-label">Vehicle</div>
                <div class="gl-rl-mob-stat-val">{{ vehicleEmoji(rider.vehicle_type) }} {{ rider.vehicle_type }}</div>
              </div>
              <div class="gl-rl-mob-stat">
                <div class="gl-rl-mob-stat-label">Deliveries</div>
                <div class="gl-rl-mob-stat-val">{{ rider.delivery_stats?.total_deliveries || 0 }}</div>
              </div>
              <div class="gl-rl-mob-stat">
                <div class="gl-rl-mob-stat-label">Earnings</div>
                <div class="gl-rl-mob-stat-val">RM {{ formatCurrency(rider.delivery_stats?.total_earned) }}</div>
              </div>
              <div class="gl-rl-mob-stat">
                <div class="gl-rl-mob-stat-label">Success Rate</div>
                <div class="gl-rl-mob-stat-val"><span class="gl-badge" :class="rateBadgeClass(rider.delivery_stats?.success_rate)">{{ rider.delivery_stats?.success_rate || 0 }}%</span></div>
              </div>
            </div>
            <div class="gl-rl-mob-footer">
              <button class="gl-btn-ghost-sm" @click="viewRider(rider)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View
              </button>
              <button class="gl-btn-ghost-sm" @click="editRider(rider)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>
              <button class="gl-btn-ghost-sm" :class="rider.is_active ? 'gl-btn-ghost-warn' : 'gl-btn-ghost-ok'" @click="toggleRider(rider)">
                {{ rider.is_active ? 'Deactivate' : 'Activate' }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div class="gl-empty-title">No riders found</div>
        <div class="gl-empty-sub">Try adjusting your filters or add a new rider</div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.lastPage > 1" class="gl-pager">
        <button class="gl-pager-btn" :disabled="pagination.currentPage === 1" @click="goToPage(pagination.currentPage - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button v-for="p in paginationPages" :key="p" class="gl-pager-btn" :class="{ 'gl-pager-active': p === pagination.currentPage, 'gl-pager-dots': p === '...' }" @click="p !== '...' && goToPage(p)">{{ p }}</button>
        <button class="gl-pager-btn" :disabled="pagination.currentPage === pagination.lastPage" @click="goToPage(pagination.currentPage + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>

    <!-- ── Create / Edit Modal ── -->
    <div v-if="showFormModal" class="gl-modal-overlay" @click.self="showFormModal=false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title">{{ formMode === 'create' ? 'Add New Rider' : 'Edit Rider' }}</div>
          <button class="gl-modal-close" @click="showFormModal=false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <form @submit.prevent="submitRiderForm" style="display:flex;flex-direction:column;gap:16px">
            <div class="gl-form-field">
              <label class="gl-form-label">Name <span class="gl-req">*</span></label>
              <input type="text" class="gl-input" v-model="riderForm.name" required placeholder="Full name">
            </div>
            <div class="gl-form-row">
              <div class="gl-form-field">
                <label class="gl-form-label">Phone <span class="gl-req">*</span></label>
                <input type="text" class="gl-input" v-model="riderForm.phone" required placeholder="e.g. 0123456789">
              </div>
              <div class="gl-form-field">
                <label class="gl-form-label">Email</label>
                <input type="email" class="gl-input" v-model="riderForm.email" placeholder="Optional">
              </div>
            </div>
            <div class="gl-form-field">
              <label class="gl-form-label">Password {{ formMode === 'edit' ? '(leave blank to keep)' : '*' }}</label>
              <input type="password" class="gl-input" v-model="riderForm.password" :required="formMode === 'create'" minlength="8" placeholder="••••••••">
              <div class="gl-field-hint">Minimum 8 characters (required for rider login).</div>
            </div>
            <div class="gl-form-field">
              <label class="gl-form-label">Outlet <span class="gl-req">*</span></label>
              <select class="gl-input" v-model="riderForm.outlet_id" required>
                <option value="">Select outlet…</option>
                <option v-for="o in outlets" :key="o.id" :value="o.id">{{ o.name }} ({{ o.company?.name || 'N/A' }})</option>
              </select>
            </div>
            <div class="gl-form-row">
              <div class="gl-form-field">
                <label class="gl-form-label">Vehicle Type <span class="gl-req">*</span></label>
                <select class="gl-input" v-model="riderForm.vehicle_type" required>
                  <option value="motorcycle">🏍️ Motorcycle</option>
                  <option value="car">🚗 Car</option>
                  <option value="bicycle">🚲 Bicycle</option>
                </select>
              </div>
              <div class="gl-form-field">
                <label class="gl-form-label">Vehicle Plate</label>
                <input type="text" class="gl-input" v-model="riderForm.vehicle_plate" placeholder="e.g. ABC1234">
              </div>
            </div>
            <div class="gl-toggle-row">
              <div>
                <div class="gl-form-label" style="margin:0">Active</div>
                <div class="gl-field-hint">Rider can log in and receive deliveries</div>
              </div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': riderForm.is_active }" @click="riderForm.is_active = !riderForm.is_active">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
          </form>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="showFormModal=false" :disabled="submitting">Cancel</button>
          <button class="gl-btn-primary" @click="submitRiderForm" :disabled="submitting">
            <span v-if="submitting" class="gl-spin"></span>
            {{ formMode === 'create' ? 'Create Rider' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Rider Detail Modal ── -->
    <div v-if="showDetailModal && selectedRider" class="gl-modal-overlay" @click.self="showDetailModal=false">
      <div class="gl-modal gl-modal-lg">
        <div class="gl-modal-header">
          <div class="gl-modal-title">Rider Profile</div>
          <button class="gl-modal-close" @click="showDetailModal=false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <!-- Profile hero -->
          <div class="gl-rl-profile-hero">
            <div class="gl-rl-avatar gl-rl-avatar-xl">{{ initials(selectedRider.rider?.name) }}</div>
            <div class="gl-rl-profile-info">
              <div class="gl-rl-profile-name">{{ selectedRider.rider?.name }}</div>
              <div class="gl-rl-profile-meta">
                <span>{{ selectedRider.rider?.phone }}</span>
                <span v-if="selectedRider.rider?.email">· {{ selectedRider.rider?.email }}</span>
              </div>
              <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
                <span class="gl-badge" :class="statusBadgeClass(selectedRider.rider?.status, selectedRider.rider?.is_active)">
                  {{ selectedRider.rider?.is_active ? selectedRider.rider?.status : 'inactive' }}
                </span>
                <span class="gl-badge gl-badge-dark">{{ vehicleEmoji(selectedRider.rider?.vehicle_type) }} {{ selectedRider.rider?.vehicle_type }}</span>
                <span v-if="selectedRider.rider?.vehicle_plate" class="gl-badge gl-badge-dark">{{ selectedRider.rider?.vehicle_plate }}</span>
                <span v-if="selectedRider.rider?.outlet" class="gl-badge gl-badge-info">{{ selectedRider.rider?.outlet?.name }}</span>
              </div>
            </div>
          </div>

          <!-- Stats grid -->
          <div class="gl-rl-stats-grid">
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val">{{ selectedRider.statistics?.total_deliveries || 0 }}</div>
              <div class="gl-rl-stat-box-label">Total Deliveries</div>
            </div>
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val" style="color:#16a34a">{{ selectedRider.statistics?.completed || 0 }}</div>
              <div class="gl-rl-stat-box-label">Completed</div>
            </div>
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val" style="color:#dc2626">{{ selectedRider.statistics?.failed || 0 }}</div>
              <div class="gl-rl-stat-box-label">Failed</div>
            </div>
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val">{{ selectedRider.statistics?.success_rate || 0 }}%</div>
              <div class="gl-rl-stat-box-label">Success Rate</div>
            </div>
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val">RM {{ formatCurrency(selectedRider.statistics?.total_earned) }}</div>
              <div class="gl-rl-stat-box-label">Total Earned</div>
            </div>
            <div class="gl-rl-stat-box">
              <div class="gl-rl-stat-box-val">{{ selectedRider.statistics?.avg_delivery_time_minutes || 0 }} min</div>
              <div class="gl-rl-stat-box-label">Avg. Time</div>
            </div>
          </div>

          <!-- Recent deliveries -->
          <div class="gl-rl-section-label">Recent Deliveries</div>
          <div v-if="selectedRider.recent_deliveries?.length" class="gl-tbl-wrap">
            <table class="gl-tbl">
              <thead><tr><th>#</th><th>Customer</th><th>Status</th><th>Fee</th><th>Date</th></tr></thead>
              <tbody>
                <tr v-for="d in selectedRider.recent_deliveries.slice(0,10)" :key="d.id">
                  <td><span class="gl-id-badge">#{{ d.id }}</span></td>
                  <td>{{ d.order?.user?.name || 'N/A' }}</td>
                  <td><span class="gl-badge" :class="statusBadgeClass(d.status)">{{ d.status }}</span></td>
                  <td>RM {{ formatCurrency(d.fee) }}</td>
                  <td>{{ formatDate(d.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="gl-empty" style="padding:28px 0">
            <div class="gl-empty-sub">No deliveries yet</div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { superAdminDeliveryApi } from '@/services/superAdminApi';

export default {
  name: 'RiderList',
  data() {
    return {
      loading: false,
      submitting: false,
      riders: [],
      outlets: [],
      riderStats: {},
      pagination: { currentPage: 1, lastPage: 1, total: 0 },
      filters: { status: '', vehicle_type: '', search: '' },
      formMode: 'create',
      riderForm: { name: '', phone: '', email: '', password: '', outlet_id: '', vehicle_type: 'motorcycle', vehicle_plate: '', is_active: true },
      selectedRider: null,
      showFormModal: false,
      showDetailModal: false,
      searchTimeout: null
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const { currentPage: c, lastPage: l } = this.pagination;
      if (l <= 7) { for (let i = 1; i <= l; i++) pages.push(i); }
      else if (c <= 3) { pages.push(1, 2, 3, 4, '...', l); }
      else if (c >= l - 2) { pages.push(1, '...', l - 3, l - 2, l - 1, l); }
      else { pages.push(1, '...', c - 1, c, c + 1, '...', l); }
      return pages;
    }
  },
  created() {
    this.fetchRiders();
    this.fetchStats();
    this.fetchOutlets();
  },
  methods: {
    async fetchRiders(page = 1) {
      this.loading = true;
      try {
        const r = await superAdminDeliveryApi.getRiders({ page, ...this.filters });
        if (r.data.success) {
          this.riders = r.data.data;
          const m = r.data.meta;
          this.pagination = { currentPage: m.current_page, lastPage: m.last_page, total: m.total };
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    async fetchStats() {
      try {
        const r = await superAdminDeliveryApi.getRiderStats();
        if (r.data.success) this.riderStats = r.data.data.stats;
      } catch (e) { console.error(e); }
    },
    async fetchOutlets() {
      try {
        const r = await superAdminDeliveryApi.getOutlets();
        if (r.data.success) this.outlets = r.data.data;
      } catch (e) { console.error(e); }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchRiders(), 500);
    },
    goToPage(p) { if (p >= 1 && p <= this.pagination.lastPage) this.fetchRiders(p); },
    openCreateModal() {
      this.formMode = 'create';
      this.riderForm = { name: '', phone: '', email: '', password: '', outlet_id: '', vehicle_type: 'motorcycle', vehicle_plate: '', is_active: true };
      this.showFormModal = true;
    },
    editRider(rider) {
      this.formMode = 'edit';
      this.riderForm = { id: rider.id, name: rider.name, phone: rider.phone, email: rider.email || '', password: '', outlet_id: rider.outlet_id, vehicle_type: rider.vehicle_type, vehicle_plate: rider.vehicle_plate || '', is_active: rider.is_active };
      this.showFormModal = true;
    },
    async viewRider(rider) {
      try {
        const r = await superAdminDeliveryApi.getRider(rider.id);
        if (r.data.success) { this.selectedRider = r.data.data; this.showDetailModal = true; }
      } catch (e) { console.error(e); }
    },
    async submitRiderForm() {
      this.submitting = true;
      try {
        const r = this.formMode === 'create'
          ? await superAdminDeliveryApi.createRider(this.riderForm)
          : await superAdminDeliveryApi.updateRider(this.riderForm.id, this.riderForm);
        if (r.data.success) {
          this.$toast?.success?.(r.data.message);
          this.showFormModal = false;
          this.fetchRiders(); this.fetchStats();
        }
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || 'Failed to save rider');
      } finally { this.submitting = false; }
    },
    async toggleRider(rider) {
      if (!confirm(`${rider.is_active ? 'Deactivate' : 'Activate'} ${rider.name}?`)) return;
      try {
        const r = await superAdminDeliveryApi.toggleRider(rider.id);
        if (r.data.success) { this.$toast?.success?.(r.data.message); this.fetchRiders(); this.fetchStats(); }
      } catch (e) { this.$toast?.error?.('Failed to update rider status'); }
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },
    vehicleEmoji(type) {
      return { motorcycle: '🏍️', car: '🚗', bicycle: '🚲' }[type] || '🚗';
    },
    statusBadgeClass(status, isActive = true) {
      if (!isActive) return 'gl-badge-gray';
      return { available: 'gl-badge-green', busy: 'gl-badge-yellow', offline: 'gl-badge-gray', delivered: 'gl-badge-green', failed: 'gl-badge-red', cancelled: 'gl-badge-gray', picking_up: 'gl-badge-info', on_the_way: 'gl-badge-blue' }[status] || 'gl-badge-gray';
    },
    rateBadgeClass(rate) {
      if (!rate) return 'gl-badge-gray';
      if (rate >= 90) return 'gl-badge-green';
      if (rate >= 70) return 'gl-badge-yellow';
      return 'gl-badge-red';
    },
    formatCurrency(v) { return v ? parseFloat(v).toFixed(2) : '0.00'; },
    formatDate(s) {
      if (!s) return '—';
      return new Date(s).toLocaleDateString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; gap: 16px; flex-wrap: wrap; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }

/* ── Stat cards ── */
.gl-stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-stat-label { font-size: 11.5px; color: #6b7f7b; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 3px; }
.gl-stat-value { font-size: 24px; font-weight: 700; color: #111827; letter-spacing: -.5px; line-height: 1.1; }

/* ── Filter bar ── */
.gl-filter-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.gl-search-wrap { position: relative; flex: 1; min-width: 200px; }
.gl-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-search { width: 100%; padding: 9px 12px 9px 34px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; }
.gl-search:focus { border-color: #900C27; }
.gl-select { padding: 9px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #374151; background: #fff; outline: none; cursor: pointer; }
.gl-select:focus { border-color: #900C27; }

/* ── Card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 16px; overflow: hidden; }

/* ── Table ── */
.gl-tbl-wrap { overflow-x: auto; }
.gl-tbl { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.gl-tbl thead tr { background: #f8faf9; }
.gl-tbl th { padding: 11px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .6px; white-space: nowrap; }
.gl-tbl td { padding: 13px 16px; border-top: 1px solid #f5f7f6; color: #374151; vertical-align: middle; }
.gl-tbl tbody tr:hover { background: #fafcfb; }

/* ── Mobile card list ── */
.gl-mob-list { display: none; flex-direction: column; gap: 0; }

/* ── Badges ── */
.gl-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 99px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-badge-green { background: #dcfce7; color: #16a34a; }
.gl-badge-yellow { background: #fef9c3; color: #ca8a04; }
.gl-badge-red { background: #fee2e2; color: #dc2626; }
.gl-badge-gray { background: #f1f5f9; color: #64748b; }
.gl-badge-info { background: #e0f2fe; color: #0284c7; }
.gl-badge-blue { background: #dbeafe; color: #2563eb; }
.gl-badge-dark { background: #f1f5f9; color: #374151; }
.gl-id-badge { font-family: 'SF Mono', monospace; font-size: 12px; font-weight: 700; color: #900C27; background: #e8f4f1; padding: 2px 6px; border-radius: 5px; }

/* ── Rider-specific ── */
.gl-rl-rider-cell { display: flex; align-items: center; gap: 10px; }
.gl-rl-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #900C27, #16a34a); color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-rl-avatar-lg { width: 48px; height: 48px; font-size: 16px; }
.gl-rl-avatar-xl { width: 64px; height: 64px; font-size: 22px; border-radius: 16px; }
.gl-rl-name { font-weight: 600; color: #111827; font-size: 13.5px; }
.gl-rl-id { font-size: 11.5px; color: #9ca3af; font-family: 'SF Mono', monospace; }
.gl-rl-contact { font-size: 13.5px; color: #374151; }
.gl-rl-sub { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-rl-actions { display: flex; gap: 4px; }

/* ── Mobile rider cards ── */
.gl-rl-mob-card { padding: 16px; border-bottom: 1px solid #f5f7f6; }
.gl-rl-mob-card:last-child { border-bottom: none; }
.gl-rl-mob-top { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
.gl-rl-mob-info { flex: 1; min-width: 0; }
.gl-rl-mob-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
.gl-rl-mob-stat { background: #f8faf9; border-radius: 8px; padding: 8px 10px; }
.gl-rl-mob-stat-label { font-size: 10.5px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 3px; }
.gl-rl-mob-stat-val { font-size: 13px; font-weight: 600; color: #111827; }
.gl-rl-mob-footer { display: flex; gap: 8px; }

/* ── Icon buttons ── */
.gl-icon-btn { width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid #e0e7e5; background: #fff; color: #6b7f7b; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all .15s; }
.gl-icon-btn:hover { border-color: #900C27; color: #900C27; background: #f0f8f6; }
.gl-icon-btn-warn { border-color: #fef9c3; color: #ca8a04; background: #fefce8; }
.gl-icon-btn-warn:hover { border-color: #ca8a04; }
.gl-icon-btn-ok { border-color: #dcfce7; color: #16a34a; background: #f0fdf4; }
.gl-icon-btn-ok:hover { border-color: #16a34a; }

/* ── Buttons ── */
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 10px 18px; font-size: 13.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.gl-btn-primary:hover { background: #1a5048; }
.gl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 10px 18px; font-size: 13.5px; font-weight: 500; cursor: pointer; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }
.gl-btn-ghost-sm { display: inline-flex; align-items: center; gap: 5px; flex: 1; justify-content: center; padding: 7px 10px; border: 1.5px solid #e0e7e5; background: #fff; border-radius: 8px; font-size: 12.5px; font-weight: 500; color: #374151; cursor: pointer; }
.gl-btn-ghost-sm:hover { border-color: #900C27; color: #900C27; }
.gl-btn-ghost-warn { border-color: #fef9c3; color: #ca8a04; }
.gl-btn-ghost-warn:hover { border-color: #ca8a04; }
.gl-btn-ghost-ok { border-color: #dcfce7; color: #16a34a; }
.gl-btn-ghost-ok:hover { border-color: #16a34a; }

/* ── Toggle ── */
.gl-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-top: 1px solid #f5f7f6; }
.gl-toggle { width: 44px; height: 24px; border-radius: 12px; background: #e0e7e5; border: none; cursor: pointer; position: relative; transition: background .2s; flex-shrink: 0; padding: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; display: block; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }

/* ── Form ── */
.gl-form-field { display: flex; flex-direction: column; gap: 6px; }
.gl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.gl-form-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-field-hint { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-req { color: #dc2626; }
.gl-input { width: 100%; padding: 10px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; transition: border-color .15s; }
.gl-input:focus { border-color: #900C27; }

/* ── Pagination ── */
.gl-pager { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 16px; border-top: 1px solid #f5f7f6; flex-wrap: wrap; }
.gl-pager-btn { min-width: 34px; height: 34px; padding: 0 8px; border-radius: 8px; border: 1.5px solid #e0e7e5; background: #fff; color: #374151; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all .15s; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-active { background: #900C27; border-color: #900C27; color: #fff; }
.gl-pager-active:hover { background: #900C27; color: #fff; }
.gl-pager-dots { border: none; cursor: default; }
.gl-pager-dots:hover { border: none; color: #374151; }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 520px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.2); display: flex; flex-direction: column; }
.gl-modal-lg { max-width: 720px; }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #111827; }
.gl-modal-close { width: 30px; height: 30px; border-radius: 8px; border: none; background: #f1f5f9; color: #6b7f7b; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.gl-modal-close:hover { background: #e0e7e5; }
.gl-modal-body { padding: 20px 24px; flex: 1; }
.gl-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #f0f3f2; background: #f8faf9; border-radius: 0 0 18px 18px; }

/* ── Profile detail ── */
.gl-rl-profile-hero { display: flex; align-items: flex-start; gap: 16px; padding: 16px; background: #f8faf9; border-radius: 12px; margin-bottom: 16px; }
.gl-rl-profile-name { font-size: 18px; font-weight: 700; color: #111827; }
.gl-rl-profile-meta { font-size: 13px; color: #6b7f7b; margin-top: 2px; }
.gl-rl-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
.gl-rl-stat-box { background: #f8faf9; border-radius: 10px; padding: 14px; text-align: center; }
.gl-rl-stat-box-val { font-size: 20px; font-weight: 700; color: #111827; letter-spacing: -.3px; }
.gl-rl-stat-box-label { font-size: 11px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; margin-top: 3px; }
.gl-rl-section-label { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 10px; }

/* ── Spinner ── */
.gl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Empty ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; }
.gl-empty-icon { width: 56px; height: 56px; border-radius: 14px; background: #f0f3f2; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 14px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.gl-empty-sub { font-size: 13px; color: #9ca3af; }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .gl-stats-row { grid-template-columns: repeat(2, 1fr); }
  .gl-rl-stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-stats-row { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-value { font-size: 20px; }
  .gl-filter-bar { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .gl-search-wrap { grid-column: 1 / -1; }
  .gl-select { width: 100%; }
  .gl-tbl-wrap { display: none; }
  .gl-mob-list { display: flex; }
  .gl-form-row { grid-template-columns: 1fr; }
  .gl-modal { border-radius: 18px 18px 0 0; max-height: 92vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
  .gl-rl-profile-hero { flex-direction: column; align-items: center; text-align: center; }
  .gl-rl-stats-grid { grid-template-columns: repeat(2, 1fr); }
}

/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-stat-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-stat-label { color: #94a3b8; }
body.dark-only .gl-stat-value { color: #f1f5f9; }
body.dark-only .gl-filter-bar .gl-search { background: #1e2a27; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-filter-bar .gl-search:focus { border-color: #900C27; }
body.dark-only .gl-select { background: #1e2a27; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-tbl thead tr { background: #162220; }
body.dark-only .gl-tbl th { color: #64748b; }
body.dark-only .gl-tbl td { border-color: #2d3f3b; color: #cbd5e1; }
body.dark-only .gl-tbl tbody tr:hover { background: #1a2522; }
body.dark-only .gl-rl-name { color: #f1f5f9; }
body.dark-only .gl-rl-mob-card { border-color: #2d3f3b; }
body.dark-only .gl-rl-mob-stat { background: #162220; }
body.dark-only .gl-rl-mob-stat-val { color: #f1f5f9; }
body.dark-only .gl-icon-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-icon-btn:hover { background: rgba(144,12,39,.15); border-color: #900C27; color: #4ade80; }
body.dark-only .gl-btn-ghost-sm { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-btn-ghost-sm:hover { border-color: #900C27; color: #4ade80; }
body.dark-only .gl-pager-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #4ade80; }
body.dark-only .gl-pager-active { background: #900C27; border-color: #900C27; color: #fff; }
body.dark-only .gl-modal { background: #1e2a27; }
body.dark-only .gl-modal-header { border-color: #2d3f3b; }
body.dark-only .gl-modal-title { color: #f1f5f9; }
body.dark-only .gl-modal-close { background: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-modal-body { background: #1e2a27; }
body.dark-only .gl-modal-footer { background: #162220; border-color: #2d3f3b; }
body.dark-only .gl-input { background: #162220; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-input:focus { border-color: #900C27; }
body.dark-only .gl-form-label { color: #e2e8f0; }
body.dark-only .gl-toggle { background: #2d3f3b; }
body.dark-only .gl-toggle-row { border-color: #2d3f3b; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-rl-profile-hero { background: #162220; }
body.dark-only .gl-rl-profile-name { color: #f1f5f9; }
body.dark-only .gl-rl-stat-box { background: #162220; }
body.dark-only .gl-rl-stat-box-val { color: #f1f5f9; }
body.dark-only .gl-badge-dark { background: rgba(241,245,249,.1); color: #e2e8f0; }
body.dark-only .gl-badge-green { background: rgba(22,163,74,.2); color: #4ade80; }
body.dark-only .gl-badge-yellow { background: rgba(217,119,6,.2); color: #fbbf24; }
body.dark-only .gl-badge-red { background: rgba(220,38,38,.2); color: #f87171; }
body.dark-only .gl-badge-gray { background: rgba(100,116,139,.2); color: #94a3b8; }
body.dark-only .gl-badge-info { background: rgba(2,132,199,.2); color: #38bdf8; }
body.dark-only .gl-badge-blue { background: rgba(37,99,235,.2); color: #60a5fa; }
body.dark-only .gl-id-badge { background: rgba(144,12,39,.3); color: #5eead4; }
body.dark-only .gl-empty-icon { background: #162220; }
body.dark-only .gl-empty-title { color: #e2e8f0; }
</style>
